import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import {
  ATTRIBUTION_MODELS,
  type AdvertisingLevel,
  type AdvertisingQuery,
  type AdvertisingReport,
  type AttributionModel,
} from '@/api/types'
import { resolvePeriod, type GlobalFilters } from '@/lib/filters'

export const DEFAULT_SORT = '-spendMinor'
/** Paid click, as the API's advertising reads default to: the Meta and ads pages credit the
 * latest paid click in Meta's own 7-day click window, so TmT's figures sit beside Meta's on
 * the same footing. The Attribution page and the rest keep last non-direct. */
export const DEFAULT_MODEL: AttributionModel = 'paid_click'
/** One page of the interactive table — the same default the API applies when a
 * request sends no `limit` (`query.DEFAULT_LIMIT`). A "Load more" click asks
 * for the next page at this same size. */
export const PAGE_SIZE = 100

const SORT_PARAM = 'adv_sort'
const SEARCH_PARAM = 'adv_q'
const MODEL_PARAM = 'adv_model'

function isAttributionModel(value: string | null): value is AttributionModel {
  return (ATTRIBUTION_MODELS as readonly string[]).includes(value ?? '')
}

/** Every React Query key this page and its drill-downs read. Keyed by the
 * *base* query — level, parent, sort, search, range — never by `limit`/`offset`,
 * which `useInfiniteQuery` tracks as pages within one cache entry. */
export const advertisingKeys = {
  all: ['advertising-campaigns'] as const,
  report: (kind: ApiClientKind, query: AdvertisingQuery) => ['advertising-campaigns', kind, query] as const,
}

/**
 * A URL-backed value with a default: reading falls back to `defaultValue`,
 * and writing removes the param entirely when the value equals it, so an
 * unmodified page keeps a plain URL. `useSort` and `useSearchTerm` are both
 * this shape; only the param name and default differ.
 */
function useUrlParam(param: string, defaultValue: string): [string, (next: string) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(param) ?? defaultValue
  const set = (next: string) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === defaultValue) updated.delete(param)
        else updated.set(param, next)
        return updated
      },
      { replace: true },
    )
  return [value, set]
}

/** The sort order lives in the URL beside the global filters, so a copied link reopens the same order. */
export function useSort(): [string, (next: string) => void] {
  return useUrlParam(SORT_PARAM, DEFAULT_SORT)
}

export function useSearchTerm(): [string, (next: string) => void] {
  return useUrlParam(SEARCH_PARAM, '')
}

/** The TmT attribution model lives in the URL beside the global filters, so a
 * copied link reopens the same model — and a stray param can never send an
 * attribution model the API does not know. */
export function useModel(): [AttributionModel, (next: AttributionModel) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(MODEL_PARAM)
  const model = isAttributionModel(raw) ? raw : DEFAULT_MODEL
  const set = (next: AttributionModel) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next === DEFAULT_MODEL) updated.delete(MODEL_PARAM)
        else updated.set(MODEL_PARAM, next)
        return updated
      },
      { replace: true },
    )
  return [model, set]
}

/**
 * The top-level campaign query: the global filter bar's date range and
 * comparison, plus this page's own sort, search and TmT model. Carries no
 * `limit`/`offset` — `useInfiniteQuery` supplies those per page. Drill-down rows
 * build their own query from `childQuery`, sharing the same range and model.
 */
export function useAdvertisingQuery(
  filters: GlobalFilters,
  sort: string,
  q: string,
  model: AttributionModel,
): AdvertisingQuery {
  return useMemo(() => {
    const { current } = resolvePeriod(filters, new Date())
    return {
      from: current.from,
      to: current.to,
      compare: filters.compare === 'previous_period',
      level: 'campaign',
      sort,
      model,
      ...(q ? { q } : {}),
    }
  }, [filters, sort, q, model])
}

/** One drill-down level's query: the same range, comparison and model, this object's children. */
export function childQuery(base: AdvertisingQuery, level: AdvertisingLevel, parentId: string): AdvertisingQuery {
  return {
    from: base.from,
    to: base.to,
    compare: base.compare,
    level,
    parentId,
    sort: DEFAULT_SORT,
    model: base.model,
  }
}

/** One page of `base`, at `offset` — what `useInfiniteQuery`'s `queryFn` sends. */
export function pageQuery(base: AdvertisingQuery, offset: number): AdvertisingQuery {
  return { ...base, limit: PAGE_SIZE, offset }
}

/** Whether a fetched page might not be the last one: it came back full. */
function pageMayContinue(rows: readonly unknown[]): boolean {
  return rows.length === PAGE_SIZE
}

export interface AdvertisingReportResult {
  /** Every row fetched so far, across all loaded pages; `undefined` until the first page lands. */
  data: AdvertisingReport | undefined
  isPending: boolean
  isError: boolean
  error: unknown
  /** The loaded page came back full, so there may be more to load. */
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

/**
 * One query's worth of campaign-report rows, paged via `useInfiniteQuery` and
 * flattened into one `AdvertisingReport` — `totals`/`totalsPrevious`/`currency`/
 * `asOf`/`attributionWindows` come from the first page (the API computes them
 * over every matching row regardless of the page requested, so every page
 * repeats the same values); only `rows` grows as more pages load. Shared by
 * the page-level table and every drill-down level, so "load more" behaves
 * identically at each.
 */
export function useAdvertisingReport(query: AdvertisingQuery): AdvertisingReportResult {
  const client = useApiClient()
  const result = useInfiniteQuery({
    queryKey: advertisingKeys.report(client.kind, query),
    queryFn: ({ pageParam }) => client.advertisingCampaigns(pageQuery(query, pageParam)),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      pageMayContinue(lastPage.rows) ? allPages.length * PAGE_SIZE : undefined,
  })
  const pages = result.data?.pages
  const data: AdvertisingReport | undefined = pages?.[0]
    ? { ...pages[0], rows: pages.flatMap((page) => page.rows) }
    : undefined
  return {
    data,
    isPending: result.isPending,
    isError: result.isError,
    error: result.error,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: () => void result.fetchNextPage(),
  }
}
